import { action, computed, observable, makeObservable } from 'mobx';
import { AppStore } from '../store';
import { CollectionEditState } from './collection.edit.state';
import { CollectionLinkState } from './collection.link.state';
import type { IkeState } from './ike.state';
import { TagSelectionState } from './tag.selection.state';

export class PageState implements IkeState {
	readonly Tag: TagSelectionState = new TagSelectionState(this);
	readonly CollectionLink: CollectionLinkState = new CollectionLinkState(this);

	@observable isGoogleMapsLoaded = false;
	@observable isCtrlShiftPressed: boolean = false;

	@observable hoverId: string | null = null;

	@observable inspectedTagId: string | null = null;
	@observable inspectedCollectionRevision: string | null = null;
	@observable inspectedCaptureId: string | null = null;
	@observable inspectedFieldId: string | null = null;

	@observable expandedTagId: string | null = null;

	constructor() {
		makeObservable(this);

		this.reset();
	}

	@action setIsCtrlShiftPressed(selected: boolean) {
		this.isCtrlShiftPressed = selected;
	}

	private _lastEditState: CollectionEditState = new CollectionEditState(null);
	@computed get CollectionEdit() {
		if (this._lastEditState.collectionRevision === this.inspectedCollectionRevision) {
			return this._lastEditState;
		}

		if (this.inspectedCollectionRevision == null) {
			this._lastEditState = new CollectionEditState(null);
			return this._lastEditState;
		}

		const collection = AppStore.Collection.get(this.inspectedCollectionRevision);
		if (collection == null) {
			this._lastEditState = new CollectionEditState(null);
			return this._lastEditState;
		}

		this._lastEditState = new CollectionEditState(collection);
		return this._lastEditState;
	}

	@action clearSelected() {
		this.inspectedCaptureId = null;
		this.inspectedCollectionRevision = null;
		this.inspectedTagId = null;
		this.expandedTagId = null;
		this.Tag.reset();
	}

	@action setHoverId(hoverId: string | null) {
		this.hoverId = hoverId;
	}

	@action setInspectedTagId(tagId: string | null): void {
		this.inspectedTagId = tagId;
	}

	@action setExpandedTagId(tagId: string | null): void {
		this.expandedTagId = tagId;
	}

	@action setInspectedCollection(revision: string | null, captureId: string | null = null) {
		const lastRevision = this.inspectedCollectionRevision;
		const lastCapture = this.inspectedCaptureId;

		this.inspectedCollectionRevision = revision;
		this.inspectedCaptureId = captureId;

		if (revision) {
			const summary = AppStore.CollectionSummary.get(revision);
			if (summary != null) {
				const firstTagId = summary.tags[0];
				this.setExpandedTagId(firstTagId ?? null);
				this.setInspectedTagId(firstTagId ?? null);
			}

			// Reset the linking field state on capture change.
			if (lastRevision === revision && lastCapture !== captureId && lastCapture != null) {
				this.CollectionEdit.cancelLinkFields();
			}
		}

		this.CollectionLink.cancelLinking();
	}

	@action setInspectedFieldId(fieldId: string | null) {
		this.inspectedFieldId = fieldId;
	}

	cancelInteractions() {
		this.CollectionEdit.cancelLinkFields();
		this.CollectionLink.cancelLinking();
	}

	async updateInspectedCollection() {
		if (this.inspectedCollectionRevision == null) {
			return;
		}

		const summary = AppStore.CollectionSummary.get(this.inspectedCollectionRevision);
		if (summary == null) {
			this.setInspectedCollection(null);
			return;
		}

		const latestSummary = await AppStore.CollectionSummary.getFetchableByUuid(summary.uuid).refetch();
		if (latestSummary == null || latestSummary.revision === summary.revision) {
			return;
		}

		this.setInspectedCollection(latestSummary.revision, this.inspectedCaptureId);
	}

	@action setInspectedCollectionFailed() {
		this.setInspectedCollection(null);
	}

	@action setGoogleMapsLoaded() {
		this.isGoogleMapsLoaded = true;
	}

	@action reset() {
		this.hoverId = null;
		this.inspectedCaptureId = null;
		this.inspectedCollectionRevision = null;
		this.inspectedTagId = null;
		this.expandedTagId = null;
		this.Tag.reset();
		this.CollectionLink.discardAllModifiedTargets();
		this.CollectionLink.cancelLinking();
	}
}

export let CurrentState: PageState;

export function initCurrentState(): PageState {
	CurrentState = new PageState();
	return CurrentState;
}
